/**
 * Google Sheet input for the Upload page (gsheet-io-01 / S5.1-S5.2, S5.5).
 *
 * Paste a link -> Connect (one read-only call) -> tick tabs -> Next. On Next the tabs
 * are imported and the caller receives the same UploadResponse a file upload produces,
 * so everything after this point (column confirmation, dialing, export) is unchanged.
 *
 * Two deliberate UI decisions, both from what the real spreadsheets look like:
 *  - Tabs with no dialable column are shown but NOT selectable. The reference sheet has
 *    five such tabs (Industry Cheat Sheet, Submitted lead examples, Search Criteria,
 *    DNC, DNC-ENG); starting a run against one of them would just burn time.
 *  - Detected columns are listed per tab, because the same column name does not mean
 *    the same thing on every tab: on the real sheet Week 4 carries its numbers in
 *    "Apollo HQ" and "Caller 1" while "leadmagic_phone" is empty.
 */

import React, { useState } from 'react';
import { gsheetsApi } from '../services/api';
import { GSheetInspectResponse, UploadResponse } from '../types';
import { canProceed, defaultPickedTabs, dialableTabs } from '../services/gsheetsUtil';

interface Props {
  onImported: (upload: UploadResponse) => void;
}

const SMALL_BTN: React.CSSProperties = {
  fontSize: 12, padding: '4px 10px', cursor: 'pointer', borderRadius: 4,
  border: '1px solid var(--border)', background: 'var(--surface-subtle)', color: 'var(--text)',
};

const GoogleSheetPicker: React.FC<Props> = ({ onImported }) => {
  const [url, setUrl] = useState('');
  const [info, setInfo] = useState<GSheetInspectResponse | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [headerRows, setHeaderRows] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<'connect' | 'import' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = async () => {
    if (!url.trim()) return;
    setBusy('connect');
    setError(null);
    try {
      const res = await gsheetsApi.inspect(url.trim());
      setInfo(res);
      setHeaderRows(Object.fromEntries(res.tabs.map((t) => [t.title, t.header_row || 1])));
      // Pre-tick every dialable tab: the common case is "run all the week tabs".
      setPicked(defaultPickedTabs(res.tabs));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not open that spreadsheet.');
      setInfo(null);
    } finally {
      setBusy(null);
    }
  };

  const next = async () => {
    if (!info || picked.length === 0) return;
    setBusy('import');
    setError(null);
    try {
      onImported(await gsheetsApi.importTabs(url.trim(), picked, headerRows));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not import those tabs.');
    } finally {
      setBusy(null);
    }
  };

  const toggle = (title: string) =>
    setPicked((cur) => (cur.includes(title) ? cur.filter((t) => t !== title) : [...cur, title]));

  const dialable = info ? dialableTabs(info.tabs) : [];
  const ready = info ? canProceed(info.tabs, picked) : false;

  return (
    <div>
      <div
        style={{
          background: 'var(--surface-subtle)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '10px 12px',
          marginBottom: 10,
          color: 'var(--text-soft)',
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        <strong style={{ color: 'var(--text-strong)' }}>Google Sheets access:</strong>{' '}
        set General access to <strong>Anyone with the link</strong> and role to{' '}
        <strong>Editor</strong> before importing. View-only links may open for review,
        but RoboDialer needs edit access to write results back to the selected rows.
      </div>

      {error && (
        <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)', padding: 10, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
          ❌ {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: info ? 12 : 0 }}>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && connect()}
          placeholder="https://docs.google.com/spreadsheets/d/…"
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 6, fontSize: 14,
            border: '1px solid var(--border)', background: 'var(--surface-subtle)', color: 'var(--text)',
          }}
        />
        <button
          onClick={connect}
          disabled={!url.trim() || busy === 'connect'}
          style={{
            padding: '10px 20px', borderRadius: 6, fontSize: 14, fontWeight: 600,
            border: '2px solid var(--teal)', cursor: !url.trim() ? 'not-allowed' : 'pointer',
            background: !url.trim() ? 'var(--status-neutral-bg)' : 'linear-gradient(135deg, #8a2000, #5c0000)',
            color: !url.trim() ? 'var(--text-faint)' : 'var(--text)', whiteSpace: 'nowrap',
          }}
        >
          {busy === 'connect' ? 'Opening…' : 'Connect'}
        </button>
      </div>

      {info && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {info.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ color: 'var(--text-soft)', fontSize: 12 }}>
                {picked.length} of {dialable.length} tab{dialable.length !== 1 ? 's' : ''} selected
              </span>
              {/* Same pair, same position and styling as the file-upload sheet list, so
                  the two pickers behave identically. "All" can only ever select tabs
                  that hold dialable numbers — a reference tab is not selectable at all. */}
              <button onClick={() => setPicked(defaultPickedTabs(info.tabs))} style={SMALL_BTN}>All</button>
              <button onClick={() => setPicked([])} style={SMALL_BTN}>None</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {info.tabs.map((tab) => {
              const on = picked.includes(tab.title);
              return (
                <div
                  key={tab.gid}
                  onClick={() => tab.has_phone_data && toggle(tab.title)}
                  title={tab.has_phone_data ? undefined : 'No dialable phone column found in this tab'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    border: `2px solid ${on ? 'var(--amber)' : 'var(--border)'}`, borderRadius: 8,
                    cursor: tab.has_phone_data ? 'pointer' : 'not-allowed',
                    opacity: tab.has_phone_data ? 1 : 0.45,
                    background: on ? 'var(--status-analysis-processing-bg)' : 'var(--surface)',
                  }}
                >
                  <input type="checkbox" checked={on} disabled={!tab.has_phone_data} onChange={() => {}} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{tab.title}</span>
                    <span style={{ color: 'var(--text-soft)', fontSize: 12, marginLeft: 8 }}>
                      {tab.has_phone_data
                        ? `${tab.likely_phone_columns.length} phone column${tab.likely_phone_columns.length !== 1 ? 's' : ''}: ${tab.likely_phone_columns.join(', ')}`
                        : 'no phone column detected'}
                    </span>
                  </div>
                  <label
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)', fontSize: 12 }}
                    title="Confirm the physical row that contains the column headers for this tab."
                  >
                    Header row
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, tab.row_count)}
                      value={headerRows[tab.title] ?? tab.header_row ?? 1}
                      onChange={(e) => {
                        const value = Math.max(1, Number(e.target.value || 1));
                        setHeaderRows((cur) => ({ ...cur, [tab.title]: value }));
                      }}
                      style={{ width: 72 }}
                    />
                    <span style={{ color: tab.header_confidence === 'high' ? 'var(--status-success-fg)' : 'var(--amber)' }}>
                      {tab.header_confidence}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={next}
              disabled={!ready || busy === 'import'}
              style={{
                background: !ready ? 'var(--status-neutral-bg)' : 'linear-gradient(135deg, #8a2000, #5c0000)',
                color: !ready ? 'var(--text-faint)' : 'var(--text)',
                border: '2px solid var(--teal)', padding: '12px 32px', borderRadius: 8,
                fontSize: 15, fontWeight: 600, cursor: !ready ? 'not-allowed' : 'pointer',
              }}
            >
              {busy === 'import' ? 'Importing…' : 'Next: Confirm Columns →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GoogleSheetPicker;
